<!-- SkyPlus Case List -->
<div class="sp-dashboard-container">
  <sp-header></sp-header>

  <main class="sp-main">
    <!-- Page header + breadcrumb -->
    <div class="sp-page-header">
      <div class="sp-greeting">
        <div class="breadcrumb">
          <a (click)="goDashboard()" class="crumb">
            <span class="material-icons">arrow_back</span> Dashboard
          </a>
        </div>
        <h1><span class="material-icons page-icon">folder_shared</span> All Cases</h1>
        <p>{{ cases.length }} of {{ allCases.length }} cases shown</p>
      </div>
      <div class="sp-header-actions">
        <button class="btn btn-primary" (click)="openNewCase()">
          <span class="material-icons">add_circle</span> New Case
        </button>
      </div>
    </div>

    <!-- Filter bar -->
    <section class="filter-bar">
      <div class="search-box">
        <span class="material-icons">search</span>
        <input type="text" [formControl]="searchControl"
               placeholder="Search by case#, title, party, source ref…" />
      </div>

      <div class="filters">
        <div class="filter-pill">
          <label>Status</label>
          <select [(ngModel)]="statusFilter" (change)="applyFilters()">
            <option *ngFor="let opt of statusOptions" [value]="opt">
              {{ opt === 'ALL' ? 'All statuses' : STATUS_LABELS[opt] }}
            </option>
          </select>
        </div>

        <div class="filter-pill">
          <label>Type</label>
          <select [(ngModel)]="typeFilter" (change)="applyFilters()">
            <option *ngFor="let opt of typeOptions" [value]="opt">
              {{ opt === 'ALL' ? 'All types' : TYPE_LABELS[opt] }}
            </option>
          </select>
        </div>

        <div class="filter-pill">
          <label>Priority</label>
          <select [(ngModel)]="priorityFilter" (change)="applyFilters()">
            <option *ngFor="let opt of priorityOptions" [value]="opt">
              {{ opt === 'ALL' ? 'All priorities' : opt }}
            </option>
          </select>
        </div>

        <div class="filter-pill">
          <label>Assignee</label>
          <select [(ngModel)]="assigneeFilter" (change)="applyFilters()">
            <option *ngFor="let opt of assignees" [value]="opt">
              {{ opt === 'ALL' ? 'All assignees' : opt }}
            </option>
          </select>
        </div>

        <label class="overdue-toggle">
          <input type="checkbox" [(ngModel)]="overdueOnly" (change)="applyFilters()" />
          <span class="material-icons">warning</span>
          Overdue only
        </label>

        <button class="reset-btn" (click)="resetFilters()" title="Reset filters">
          <span class="material-icons">refresh</span>
        </button>
      </div>
    </section>

    <!-- Loading state -->
    <div class="sp-loading" *ngIf="isLoading">
      <span class="material-icons spinning">autorenew</span>
      <p>Loading cases...</p>
    </div>

    <!-- Cases table -->
    <section class="cases-table-wrapper" *ngIf="!isLoading">
      <table class="cases-table" *ngIf="cases.length > 0">
        <thead>
          <tr>
            <th class="col-priority sortable" (click)="onSort('priority')">
              <span class="material-icons">flag</span>
              <ng-container *ngIf="sortField === 'priority'">
                <span class="material-icons sort-arrow">{{ sortDir === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down' }}</span>
              </ng-container>
            </th>
            <th class="col-id">Case #</th>
            <th class="col-title">Title</th>
            <th class="col-type">Type</th>
            <th class="col-status">Status</th>
            <th class="col-party">Opposing Party</th>
            <th class="col-court">Court</th>
            <th class="col-next sortable" (click)="onSort('nextEventDate')">
              Next Event
              <ng-container *ngIf="sortField === 'nextEventDate'">
                <span class="material-icons sort-arrow">{{ sortDir === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down' }}</span>
              </ng-container>
            </th>
            <th class="col-exposure sortable right" (click)="onSort('exposure')">
              Exposure
              <ng-container *ngIf="sortField === 'exposure'">
                <span class="material-icons sort-arrow">{{ sortDir === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down' }}</span>
              </ng-container>
            </th>
            <th class="col-assignee">Assignee</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let c of pagedCases; trackBy: trackById"
              (click)="openCase(c)"
              [class.closed-row]="c.status === 'CLOSED'">
            <td class="col-priority">
              <span class="priority-dot"
                    [style.background-color]="PRIORITY_COLORS[c.priority]"
                    [title]="c.priority"></span>
            </td>
            <td class="col-id">
              <span class="case-id">{{ c.id }}</span>
            </td>
            <td class="col-title">
              <div class="title-cell">
                <div class="title-main">{{ c.title }}</div>
                <div class="title-meta" *ngIf="c.sourceRef">
                  <span class="material-icons">link</span>
                  Source: {{ c.sourceRef }}
                </div>
              </div>
            </td>
            <td class="col-type">
              <span class="type-pill">{{ TYPE_LABELS[c.type] }}</span>
            </td>
            <td class="col-status">
              <span class="status-pill" [style.background-color]="STATUS_COLORS[c.status]">
                {{ STATUS_LABELS[c.status] }}
              </span>
            </td>
            <td class="col-party">
              <div class="party-cell">
                <div>{{ c.opposingParty }}</div>
                <div class="party-counsel" *ngIf="c.opposingCounsel">
                  <span class="material-icons">work</span>{{ c.opposingCounsel }}
                </div>
              </div>
            </td>
            <td class="col-court">
              <div class="court-cell">
                <div>{{ c.court || '—' }}</div>
                <div class="court-case-no" *ngIf="c.courtCaseNumber">{{ c.courtCaseNumber }}</div>
              </div>
            </td>
            <td class="col-next" [class]="'evt-' + nextEventClass(c.nextEventDate)">
              <ng-container *ngIf="c.nextEventDate; else noEvent">
                <div class="next-date">{{ formatDate(c.nextEventDate) }}</div>
                <div class="next-relative">
                  <ng-container *ngIf="daysUntil(c.nextEventDate)! < 0">{{ -daysUntil(c.nextEventDate)! }}d overdue</ng-container>
                  <ng-container *ngIf="daysUntil(c.nextEventDate)! === 0">Today</ng-container>
                  <ng-container *ngIf="daysUntil(c.nextEventDate)! > 0">in {{ daysUntil(c.nextEventDate) }}d</ng-container>
                </div>
              </ng-container>
              <ng-template #noEvent><span class="muted">—</span></ng-template>
            </td>
            <td class="col-exposure right">
              {{ formatCurrency(c.exposureAmount, true) }}
            </td>
            <td class="col-assignee">
              <div class="assignee-cell">
                <div class="assignee-avatar">{{ c.assignedTo.charAt(0) }}</div>
                <div class="assignee-name">{{ c.assignedTo }}</div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="empty-cases" *ngIf="cases.length === 0">
        <span class="material-icons">search_off</span>
        <h3>No cases match these filters</h3>
        <p>Try adjusting search or clear filters to see all cases.</p>
        <button class="btn btn-secondary" (click)="resetFilters()">Clear filters</button>
      </div>

      <!-- Pagination -->
      <div class="pagination" *ngIf="pageCount > 1">
        <button (click)="prevPage()" [disabled]="currentPage === 1" class="page-btn">
          <span class="material-icons">chevron_left</span>
        </button>
        <div class="page-info">Page {{ currentPage }} of {{ pageCount }}</div>
        <button (click)="nextPage()" [disabled]="currentPage === pageCount" class="page-btn">
          <span class="material-icons">chevron_right</span>
        </button>
      </div>
    </section>
  </main>
</div>
